// SPDX-License-Identifier: MIT
// « L'encre du forfait » — jauge de forfait Claude (desktop only). Composant
// fidèle à la maquette approuvée /__mock/usage-gauge-v1/ (2026-06-15). Rend un
// WindowState (cf platform/usage-types) en DOM honnête : ruban d'encre discret
// au repos, expressif à l'approche, carte de reprise quand la limite est atteinte.
// Honnêteté (spec §4) : tout est étiqueté « estimé · local », jamais « quota
// officiel Anthropic » ; pas de % inventé quand le plafond n'est pas fiable.
import { t } from '../i18n/t';
import type { Locale } from '../i18n/detect';
import type { WindowState } from '../platform/usage-types';

const NIB_SMALL =
  '<svg class="ink-gauge__nib" viewBox="0 0 15 18" fill="none" aria-hidden="true">' +
  '<path d="M7.5 1.2 13 12c0 3-2.4 4.8-5.5 4.8S2 15 2 12L7.5 1.2Z" stroke="currentColor" stroke-width="1.3" fill="color-mix(in srgb,currentColor 16%,transparent)"/>' +
  '<path d="M7.5 9v6" stroke="currentColor" stroke-width="1.3"/></svg>';
const NIB_BIG =
  '<svg class="ink-card__nib" viewBox="0 0 26 30" fill="none" aria-hidden="true">' +
  '<path d="M13 2 22 20c0 5-4 8-9 8s-9-3-9-8L13 2Z" stroke="currentColor" stroke-width="1.6" fill="color-mix(in srgb,currentColor 10%,transparent)"/>' +
  '<path d="M13 15v10" stroke="currentColor" stroke-width="1.6"/></svg>';

export type GaugeVariant = 'ok' | 'approaching' | 'throttled' | 'status-only';

export interface GaugeView {
  variant: GaugeVariant;
  showBar: boolean;        // false quand pas de plafond fiable → statut seul, pas de %
  fillPct: number;         // 0..100 (clampé) pour la largeur de la barre d'encre
  hue: 'sage' | 'orange' | 'muted';
}

// Cœur PUR : dérive le modèle d'affichage d'un WindowState. Testable sans DOM.
export function gaugeView(state: WindowState): GaugeView {
  const variant: GaugeVariant =
    state.status === 'throttled' ? 'throttled'
    : state.status === 'approaching' ? 'approaching'
    : state.fillPct == null ? 'status-only'
    : 'ok';
  const showBar = state.fillPct != null && variant !== 'status-only';
  const fillPct = Math.max(0, Math.min(100, state.fillPct ?? 0));
  const hue = variant === 'approaching' || variant === 'throttled' ? 'orange'
    : variant === 'status-only' ? 'muted' : 'sage';
  return { variant, showBar, fillPct, hue };
}

// La jauge ne s'affiche que s'il y a un vrai signal — sinon elle se masque
// (dégradation gracieuse §4 : pas de « ça tourne » quand rien n'a tourné).
export function gaugeHasSignal(state: WindowState): boolean {
  return state.resetsAt != null || state.fillPct != null || state.windowTokens > 0;
}

const HUE_VAR: Record<GaugeView['hue'], string> = {
  sage: 'var(--sage-deep)', orange: 'var(--orange-deep)', muted: 'var(--muted)',
};

// HH:MM dans l'heure LOCALE de l'user (le reset s'affiche dans son fuseau).
export function formatResetTime(resetsAt: number | null, locale: Locale = 'fr'): string {
  if (resetsAt == null) return '';
  return new Date(resetsAt * 1000).toLocaleTimeString(locale === 'fr' ? 'fr-FR' : 'en-GB', {
    hour: '2-digit', minute: '2-digit',
  });
}

export interface InkGaugeOptions { locale?: Locale; }

// Le RUBAN (ligne diag) — états ok / approaching / status-only. Le throttled
// est résumé compact ici (« à sec ») ; la carte complète vit dans la chemise.
export function renderInkGauge(state: WindowState, opts: InkGaugeOptions = {}): HTMLElement {
  const v = gaugeView(state);
  const time = formatResetTime(state.resetsAt, opts.locale);

  const el = document.createElement('span');
  el.className = 'ink-gauge';
  if (v.variant === 'approaching' || v.variant === 'throttled') el.classList.add('ink-gauge--warn');
  if (v.variant === 'status-only') el.classList.add('ink-gauge--statusonly');
  el.dataset.variant = v.variant;
  el.style.setProperty('--fill', `${v.fillPct}%`);
  el.style.setProperty('--hue', HUE_VAR[v.hue]);
  el.setAttribute('role', 'status');
  el.setAttribute('aria-live', 'polite');

  // nib
  const nib = document.createElement('span');
  nib.innerHTML = NIB_SMALL;
  el.appendChild(nib.firstElementChild as SVGElement);

  // bar (omise en status-only)
  if (v.showBar) {
    const bar = document.createElement('span');
    bar.className = 'ink-gauge__bar';
    const ink = document.createElement('span');
    ink.className = 'ink-gauge__ink';
    bar.appendChild(ink);
    el.appendChild(bar);
  }

  // label (par variante, fidèle à la maquette)
  const label = document.createElement('span');
  label.className = 'ink-gauge__label';
  label.innerHTML = labelHtml(v.variant, state, time);
  el.appendChild(label);

  // note manuscrite (approaching) OU étiquette estimé (repos / status-only)
  if (v.variant === 'approaching') {
    const hand = document.createElement('span');
    hand.className = 'ink-gauge__hand';
    hand.textContent = t('usage.gauge.approachNote');
    el.appendChild(hand);
  } else {
    const est = document.createElement('span');
    est.className = 'ink-gauge__est';
    est.textContent = t('usage.gauge.estLocal');
    el.appendChild(est);
  }
  return el;
}

// Placeholder « en attente du premier build ». Après le consentement §5 mais
// avant qu'un run ait écrit le moindre signal, la jauge ne se MASQUE plus (c'était
// déroutant juste après « Activer » — elle disparaissait). Elle montre un encrier
// chargé devant une page encore vierge : la barre est une ligne réglée quasi vide
// (encre à 0 % → fond pointillé papier + un fin trait de plume au bord gauche) et
// une note manuscrite dit ce qui la remplira. Calme, dormant-mais-prêt — jamais
// une erreur ni un « ça
// tourne » mensonger (dégradation gracieuse §4).
export function renderInkGaugeAwaiting(_opts: InkGaugeOptions = {}): HTMLElement {
  const el = document.createElement('span');
  el.className = 'ink-gauge ink-gauge--awaiting';
  el.dataset.variant = 'awaiting';
  el.style.setProperty('--fill', '0%');
  el.style.setProperty('--hue', HUE_VAR.sage);
  el.setAttribute('role', 'status');
  el.setAttribute('aria-live', 'polite');

  // plume chargée
  const nib = document.createElement('span');
  nib.innerHTML = NIB_SMALL;
  el.appendChild(nib.firstElementChild as SVGElement);

  // barre = ligne réglée vierge (encre à 0 %)
  const bar = document.createElement('span');
  bar.className = 'ink-gauge__bar';
  const ink = document.createElement('span');
  ink.className = 'ink-gauge__ink';
  bar.appendChild(ink);
  el.appendChild(bar);

  // label : ⏳ en attente de ton premier build
  const label = document.createElement('span');
  label.className = 'ink-gauge__label';
  label.textContent = `⏳ ${t('usage.gauge.awaiting')}`;
  el.appendChild(label);

  // note manuscrite : ce qui remplira l'encrier
  const hand = document.createElement('span');
  hand.className = 'ink-gauge__hand';
  hand.textContent = t('usage.gauge.awaitingHint');
  el.appendChild(hand);

  return el;
}

function labelHtml(variant: GaugeVariant, state: WindowState, time: string): string {
  const reset = time ? t('usage.gauge.reset').replace('{time}', time) : '';
  // Préfixe « Claude Max » / « Claude Pro » quand le type de forfait est connu.
  const plan = state.planLabel ? `${t('usage.gauge.planPrefix').replace('{plan}', state.planLabel)} · ` : '';
  if (variant === 'approaching') {
    const mins = state.projectionMins != null ? String(state.projectionMins) : '?';
    const head = t('usage.gauge.approaching').replace('{mins}', `<b>${mins}</b>`);
    return `${plan}${time ? `${head} · ${t('usage.gauge.resetAt').replace('{time}', `<b>${time}</b>`)}` : head}`;
  }
  if (variant === 'throttled') {
    return time ? `<b>${t('usage.gauge.dry')}</b> · ${reset}` : `<b>${t('usage.gauge.dry')}</b>`;
  }
  if (variant === 'status-only') {
    // Avec forfait connu : « Claude Max : ça tourne » ; sinon « forfait Claude : ça tourne ».
    const lead = state.planLabel ? t('usage.gauge.planPrefix').replace('{plan}', state.planLabel) : t('usage.gauge.label');
    const head = `${lead} : <b>${t('usage.gauge.running')}</b>`;
    return time ? `${head} · ${t('usage.gauge.resetEst').replace('{time}', time)}` : head;
  }
  // ok
  const pct = `<b>${t('usage.gauge.fill').replace('{pct}', String(gaugeView(state).fillPct))}</b>`;
  return `${plan}${time ? `${pct} · ${reset}` : pct}`;
}

export interface ConsentLineOptions { locale?: Locale; onGrant?: () => void; }

// §5 : ligne de consentement unique avant de lire ~/.claude/projects (compteurs
// seuls, jamais le contenu, rien n'est envoyé). Sans elle, la jauge tourne en
// stream-only (statut/reset, pas de %).
export function renderConsentLine(opts: ConsentLineOptions = {}): HTMLElement {
  const el = document.createElement('span');
  el.className = 'ink-gauge ink-gauge__consent';
  el.dataset.component = 'usage-consent';
  const lock = document.createElement('span');
  lock.className = 'ink-gauge__est';
  lock.textContent = `🔒 ${t('usage.consent.prompt')}`;
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'ink-gauge__consent-btn';
  btn.textContent = t('usage.consent.activate');
  btn.addEventListener('click', () => opts.onGrant?.());
  el.append(lock, btn);
  return el;
}

export interface InkGaugeCardOptions {
  locale?: Locale;
  showUpsell?: boolean;                 // déverrouillé au symptôme répété seulement
  onResume?: () => void;                // « Reprendre à HH:MM »
  onSwitchPlan?: () => void;            // « Continuer sur un autre forfait »
  onUpsell?: () => void;                // clic « Claude Max »
}

// La CARTE throttled (chemise / fil d'état). Honnête d'abord : reprise gratuite
// en primaire, upsell Max séparé et seulement au symptôme répété.
export function renderInkGaugeCard(state: WindowState, opts: InkGaugeCardOptions = {}): HTMLElement {
  const time = formatResetTime(state.resetsAt, opts.locale) || '—';

  const card = document.createElement('div');
  card.className = 'ink-card';
  card.dataset.variant = 'throttled';

  const row = document.createElement('div');
  row.className = 'ink-card__row';
  const nib = document.createElement('span');
  nib.innerHTML = NIB_BIG;
  row.appendChild(nib.firstElementChild as SVGElement);

  const col = document.createElement('div');
  const title = document.createElement('div');
  title.className = 'ink-card__title';
  title.textContent = t('usage.card.title');
  const sub = document.createElement('p');
  sub.className = 'ink-card__sub';
  // « … se recharge à {time} … » avec le temps stylé, sans innerHTML dynamique
  const [pre, post] = t('usage.card.sub').split('{time}');
  sub.append(document.createTextNode(pre ?? ''));
  const when = document.createElement('span');
  when.className = 'ink-card__when';
  when.textContent = time;
  sub.append(when, document.createTextNode(post ?? ''));
  col.append(title, sub);
  row.appendChild(col);
  card.appendChild(row);

  const actions = document.createElement('div');
  actions.className = 'ink-card__actions';
  const resume = document.createElement('button');
  resume.type = 'button';
  resume.className = 'ink-gauge-btn ink-gauge-btn--primary';
  resume.textContent = t('usage.card.resume').replace('{time}', time);
  resume.addEventListener('click', () => opts.onResume?.());
  const sw = document.createElement('button');
  sw.type = 'button';
  sw.className = 'ink-gauge-btn ink-gauge-btn--ghost';
  sw.textContent = t('usage.card.switch');
  sw.addEventListener('click', () => opts.onSwitchPlan?.());
  actions.append(resume, sw);
  card.appendChild(actions);

  // upsell — seulement au symptôme répété
  if (opts.showUpsell) {
    const up = document.createElement('p');
    up.className = 'ink-card__upsell';
    const [upPre, upPost] = t('usage.card.upsell').split('{link}');
    up.append(document.createTextNode(upPre ?? ''));
    const a = document.createElement('a');
    a.href = '/tarifs';
    a.textContent = t('usage.card.upsellLink');
    a.addEventListener('click', (e) => { if (opts.onUpsell) { e.preventDefault(); opts.onUpsell(); } });
    up.append(a, document.createTextNode(upPost ?? ''));
    card.appendChild(up);
  }

  const priv = document.createElement('p');
  priv.className = 'ink-card__priv';
  priv.textContent = t('usage.card.privacy');
  card.appendChild(priv);

  return card;
}
